import React, { createContext, useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { Permission, useValidateSession, WebsiteTab } from '../hooks/useValidateSession';

interface UnauthenticatedSessionContext {
	sessionId: null;
	userId: null;
	firstName: null;
	lastName: null;
	email: null;
	position: null;
	isAdmin: null;
	username: null;
	organizationId: null;
	organizationName: null;
	organizationMode: null;
	demoMode: null;
	pylonEmailHash: null;
	locationName: null;
	locationId: null;
	rekeyingEnabled: null;
	onboardingStep: null;
	inventoryEnabled: null;
	invoicingEnabled: null;
	deliveryReceiveEnabled: null;
	deliveryReceiveEnabledAt: null;
	askCarlAllowed: null;
	isInternal: null;
	canManageInternalTeam: null;
	isAuthenticated: false;
	tabs: null,
	permissions: null,
	login: (sessionId: string) => void;
	logout: () => void;
	adoptSwitchedSession: (sessionId: string) => void;
	loading: boolean;
}

interface AuthenticatedSessionContext {
	sessionId: string;
	userId: string;
	firstName: string;
	lastName: string;
	email: string;
	position: string;
	isAdmin: boolean;
	username: string;
	organizationId: string;
	organizationName: string;
	organizationMode: 'full-time' | 'invoicing' | 'inventory';
	demoMode: boolean;
	pylonEmailHash: string;
	locationId: Array<string> | 'all';
	locationName: string;
	rekeyingEnabled: boolean;
	permissions: Array<Permission>;
	tabs: Array<WebsiteTab>;
	onboardingStep: string | null;
	inventoryEnabled: boolean;
	invoicingEnabled: boolean;
	deliveryReceiveEnabled: boolean;
	deliveryReceiveEnabledAt: string | null;
	askCarlAllowed: boolean;
	/** Yaxle employee account: can switch between every customer organization. */
	isInternal: boolean;
	/** Employee who may invite employees and change their access. */
	canManageInternalTeam: boolean;
	isAuthenticated: true;
	login: (sessionId: string) => void;
	logout: () => void;
	/**
	 * Takes over the session `POST /internal/switch-organization` issued: drops every
	 * org-scoped cache and filter, then revalidates against the new org.
	 */
	adoptSwitchedSession: (sessionId: string) => void;
	loading: boolean;
}

export type SessionContextType = UnauthenticatedSessionContext | AuthenticatedSessionContext;

/** True when the signed-in org is invoicing-only (not full-time collision workflow). */
export function isInvoicingOrgSession(ctx: SessionContextType | undefined): boolean {
	return ctx != null && ctx.isAuthenticated === true && ctx.organizationMode === 'invoicing';
}

/** True when the signed-in org is inventory-only (Inventory tab, plus Reporting for executives/GMs). */
export function isInventoryOrgSession(ctx: SessionContextType | undefined): boolean {
	return ctx != null && ctx.isAuthenticated === true && ctx.organizationMode === 'inventory';
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

/** LocationSelector filters hold location ids, which belong to one organization. */
const ORG_SCOPED_STORAGE_KEYS = ['location_ids', 'delivery_board_location_ids', 'inventory_location_ids'] as const;
/** List-page view caches (filters, location ids) and the Settings "Back" target, all from the previous org. */
const ORG_SCOPED_SESSION_KEYS = [
	'home_view_state_cache',
	'dashboard_view_state_cache',
	'invoices_view_state_cache',
	'settingsReturnPath',
] as const;

interface SessionProviderProps {
	children: React.ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
	const [sessionId, setSessionId] = useState<string | null>(() => localStorage.getItem('session_id'));
	const lastIdentifiedUserIdRef = useRef<string | null>(null);
	const queryClient = useQueryClient();

	const clearSharedLocationState = useCallback(() => {
		localStorage.removeItem('location_ids');
		queryClient.removeQueries();
	}, [queryClient]);

	const logout = useCallback(() => {
		clearSharedLocationState();
		localStorage.removeItem('session_id');
		setSessionId(null);
		posthog.reset();
		lastIdentifiedUserIdRef.current = null;
	}, [clearSharedLocationState]);

	const login = useCallback((newSessionId: string) => {
		clearSharedLocationState();
		localStorage.setItem('session_id', newSessionId);
		setSessionId(newSessionId);
	}, [clearSharedLocationState]);

	const adoptSwitchedSession = useCallback((newSessionId: string) => {
		for (const key of ORG_SCOPED_STORAGE_KEYS) {
			localStorage.removeItem(key);
			localStorage.removeItem(`${key}-employee`);
		}
		for (const key of ORG_SCOPED_SESSION_KEYS) {
			sessionStorage.removeItem(key);
		}
		queryClient.removeQueries();
		localStorage.setItem('session_id', newSessionId);
		setSessionId(newSessionId);
	}, [queryClient]);

	const { data: sessionData, isLoading } = useValidateSession(sessionId);

	useEffect(() => {
		if (!sessionData) return;

		if (!sessionData.sessionExists) {
			logout();
		}
	}, [sessionData, logout]);

	useEffect(() => {
		if (!sessionData?.sessionExists || !sessionData.userId) return;
		// Keyed on user and org: employees switch orgs without a new login.
		const identityKey = `${sessionData.userId}:${sessionData.organizationId}`;
		if (lastIdentifiedUserIdRef.current === identityKey) return;

		posthog.identify(sessionData.userId, {
			email: sessionData.email,
			name: `${sessionData.firstName} ${sessionData.lastName}`,
			organization_id: sessionData.organizationId,
			organization_name: sessionData.organizationName,
			position: sessionData.position,
			is_admin: sessionData.isAdmin,
			location_name: sessionData.locationName,
		});
		if (sessionData.organizationId) {
			posthog.group('organization', sessionData.organizationId, {
				name: sessionData.organizationName,
				mode: sessionData.organizationMode,
			});
		}
		lastIdentifiedUserIdRef.current = identityKey;
	}, [sessionData]);

	let value: SessionContextType = {
		sessionId: null,
		userId: null,
		isAuthenticated: false,
		organizationId: null,
		firstName: null,
		lastName: null,
		email: null,
		position: null,
		isAdmin: null,
		username: null,
		locationId: null,
		rekeyingEnabled: null,
		tabs: null,
		onboardingStep: null,
		organizationName: null,
		organizationMode: null,
		demoMode: null,
		pylonEmailHash: null,
		locationName: null,
		permissions: null,
		inventoryEnabled: null,
		invoicingEnabled: null,
		deliveryReceiveEnabled: null,
		deliveryReceiveEnabledAt: null,
		askCarlAllowed: null,
		isInternal: null,
		canManageInternalTeam: null,
		login: login,
		logout: logout,
		adoptSwitchedSession,
		loading: isLoading
	};
	if (sessionData?.sessionExists && sessionId) {
		value = {
			sessionId,
			userId: sessionData.userId,
			firstName: sessionData.firstName,
			lastName: sessionData.lastName,
			email: sessionData.email,
			position: sessionData.position,
			isAdmin: sessionData.isAdmin,
			username: sessionData.username,
			organizationId: sessionData.organizationId,
			locationId: sessionData.locationId,
			organizationName: sessionData.organizationName,
			organizationMode: sessionData.organizationMode,
			demoMode: sessionData.demoMode === true,
			pylonEmailHash: sessionData.pylonEmailHash,
			rekeyingEnabled: sessionData.rekeyingEnabled,
			onboardingStep: sessionData.onboardingStep ?? null,
			locationName: sessionData.locationName,
			isAuthenticated: true,
			tabs: sessionData.tabs,
			permissions: sessionData.permissions,
			inventoryEnabled: sessionData.inventoryEnabled,
			invoicingEnabled: sessionData.invoicingEnabled,
			deliveryReceiveEnabled: sessionData.deliveryReceiveEnabled === true,
			deliveryReceiveEnabledAt: sessionData.deliveryReceiveEnabledAt ?? null,
			askCarlAllowed: sessionData.askCarlAllowed === true,
			isInternal: sessionData.isInternal === true,
			canManageInternalTeam: sessionData.canManageInternalTeam === true,
			login,
			logout,
			adoptSwitchedSession,
			loading: isLoading,
		};
	}

	return (
		<SessionContext.Provider value={value}>
			{children}
		</SessionContext.Provider>
	);
};

export default SessionContext;